/**
 * Newsletter Popup Component
 * Handles backdrop visibility and diagonal lines border for the newsletter popup
 */

import ee from '../../ee';
import { getAssetPath, testImageLoad } from '../../utils/asset-path';

class NewsletterPopup {
  constructor(container = document) {
    this.container = container;
    this.popup = null;
    this.backdrop = null;
    this.diagonalLinesPath = getAssetPath('bg-diagonal-lines.png');
    
    this.init();
  }
  
  init() {
    this.findElements();
    this.bindEvents();
    this.updateBackdrop(); // Initial check
    this.setupDiagonalLines(); // Setup diagonal lines border
  }
  
  findElements() {
    this.popup = this.container.querySelector('newsletter-popup.pop-in');
    this.backdrop = this.container.querySelector('.newsletter-popup-backdrop');
  }
  
  bindEvents() {
    if (!this.popup || !this.backdrop) return;
    
    // Watch for attribute changes on the popup
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'hidden' || mutation.attributeName === 'open') {
          this.updateBackdrop();
        }
      });
    });
    
    this.observer.observe(this.popup, { 
      attributes: true, 
      attributeFilter: ['hidden', 'open'] 
    });
    
    // Listen for popup close events
    this.popup.addEventListener('close', () => this.updateBackdrop());
    this.popup.addEventListener('cancel', () => this.updateBackdrop());
  }
  
  updateBackdrop() {
    if (!this.popup || !this.backdrop) return;
    
    const isVisible = this.popup.hasAttribute('open') && !this.popup.hasAttribute('hidden');
    this.backdrop.classList.toggle('is-visible', isVisible);
  }
  
  setupDiagonalLines() {
    if (!this.popup) return;
    
    // Test if the diagonal lines image is available
    testImageLoad(this.diagonalLinesPath)
      .then(success => {
        if (!success) {
          console.warn('Could not load diagonal lines background image for newsletter popup');
          return;
        }
        
        // Apply the border image with proper CDN path
        this.applyDiagonalLinesBorder();
      })
      .catch(error => {
        console.error('Error testing diagonal lines image availability for newsletter popup:', error);
      });
  }
  
  applyDiagonalLinesBorder() {
    if (!this.popup) return;
    
    // Check if current brand should have background images
    const currentBrand = this.getCurrentBrand();
    if (currentBrand === 'connaught' || currentBrand === 'berkeley') {
      console.log('Skipping newsletter popup background image for', currentBrand, 'brand');
      return;
    }
    
    // Create and apply styles for the newsletter popup
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      newsletter-popup.pop-in,
      .newsletter-popup.pop-in {
        background-image: url('${this.diagonalLinesPath}') !important;
        background-size: cover !important;
        background-position: center !important;
        background-repeat: no-repeat !important;
        border: none !important;
        padding: 20px !important;
      }
      
      newsletter-popup.pop-in > *,
      .newsletter-popup.pop-in > * {
        background-color: white !important;
        padding: 2rem !important;
        margin: 0 !important;
      }
      
      @media screen and (min-width: 750px) {
        newsletter-popup.pop-in,
        .newsletter-popup.pop-in {
          padding: 25px !important;
        }
        
        newsletter-popup.pop-in > *,
        .newsletter-popup.pop-in > * {
          padding: 2.5rem !important;
        }
      }
    `;
    document.head.appendChild(styleElement);
    
    console.log('Applied newsletter popup background image with cover effect:', this.diagonalLinesPath);
  }
  
  getCurrentBrand() {
    // Check for brand class on body or html element
    const body = document.body;
    const html = document.documentElement;
    
    if (body.classList.contains('brand-connaught') || html.classList.contains('brand-connaught')) {
      return 'connaught';
    }
    if (body.classList.contains('brand-berkeley') || html.classList.contains('brand-berkeley')) {
      return 'berkeley';
    }
    
    // Default to claridges
    return 'claridges';
  }
  
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

export default NewsletterPopup; 